import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import { recordAnalyticsEvent } from "@/app/actions/analytics";
import { getAnalyticsPlatform } from "@/lib/analytics/platform";
import { getSessionId } from "@/lib/analytics/session";

const AUTH_RECORDED_KEY_PREFIX = "la_auth_";
const NEW_USER_WINDOW_MS = 10 * 60 * 1000;

/** True when the account was created within the last 10 minutes. */
export function isRecentlyCreatedUser(
  createdAt: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!createdAt) return false;
  const created = Date.parse(createdAt);
  if (Number.isNaN(created)) return false;
  return now - created >= 0 && now - created <= NEW_USER_WINDOW_MS;
}

/**
 * Records one `auth_session` event per browser session per user so a signup
 * can be joined to its acquisition (`session_start`) by session_id even when
 * the new user leaves without viewing anything. The server action resolves
 * user_id from the auth cookie; the client user id only keys deduplication.
 *
 * Called from the app-wide auth listener (useSessionKeepAlive). Every login
 * path (OAuth/email-confirm callbacks, email login, Naver/LINE, native
 * deep-link setSession) ends in a full page load or a client SIGNED_IN, so
 * INITIAL_SESSION / SIGNED_IN covers them all.
 */
export function trackAuthSession(
  event: AuthChangeEvent,
  session: Session | null,
): void {
  if (event !== "INITIAL_SESSION" && event !== "SIGNED_IN") return;
  const user = session?.user;
  if (!user?.id || typeof window === "undefined") return;

  const key = `${AUTH_RECORDED_KEY_PREFIX}${user.id}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
    // Set before the call so StrictMode / a second listener cannot double-fire.
    window.sessionStorage.setItem(key, "1");
  } catch {
    // Continue without deduplication when storage is unavailable.
  }

  void recordAnalyticsEvent({
    eventType: "auth_session",
    sessionId: getSessionId(),
    metadata: {
      platform: getAnalyticsPlatform(),
      is_new_user: isRecentlyCreatedUser(user.created_at),
    },
  }).catch(() => {});
}
